// Coerce "should be a string" values that reach the UI from Firestore or the
// AI into strings, so a shape change can never surface as
// "t.split is not a function" in the error boundary.
//
// WHY THIS EXISTS. Resume fields have drifted over time: `responsibilities`
// can be a string OR an array of bullets, `dates` a string OR { start, end },
// `createdTime` a moment string OR a Firestore Timestamp, and any AI reply
// that happens to be valid JSON arrives from axios already parsed into an
// object. Optional chaining (`dates?.split`) only guards null - an array or
// an object still throws. Route through asText() instead.

const DATE_LIKE_KEYS = ["text", "value", "label", "name", "start", "end"];

export const asText = (value, { joinWith = "\n" } = {}) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => asText(item, { joinWith }))
      .filter(Boolean)
      .join(joinWith);
  }

  // Firestore Timestamp (has toDate) or a Date.
  if (typeof value.toDate === "function") {
    try {
      return value.toDate().toISOString();
    } catch (error) {
      return "";
    }
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? "" : value.toISOString();
  }

  // A structured value where a string used to be, e.g. { start, end } for a
  // date range or { text } for a bullet. Joined with " - " so a date range
  // reads "Jan 2020 - Mar 2022", the format the date helpers already parse.
  if (typeof value === "object") {
    return DATE_LIKE_KEYS.map((key) => value[key])
      .filter((part) => typeof part === "string" && part.trim())
      .join(" - ");
  }

  return "";
};

/** `value.split(separator)` that cannot throw. */
export const splitText = (value, separator) => asText(value).split(separator);

/**
 * Sentences or bullets for display. An array is already a list; a string is
 * split on full stops the way the resume previews always have.
 */
export const toSentenceList = (value) => {
  const items = Array.isArray(value)
    ? value.map((item) => asText(item))
    : asText(value).split(".");

  return items.filter((item) => item.trim().length !== 0);
};

/**
 * One flowing, ATS-safe line of skills: "A  •  B  •  C". Accepts an array or
 * a comma/newline-separated string. A single line parses as one skills field
 * and stays aligned; a grid of per-skill bullets neither wraps evenly nor
 * reads in order for older parsers.
 */
export const SKILL_SEPARATOR = "  \u2022  ";

export const joinSkills = (skills) => {
  const items = Array.isArray(skills)
    ? skills.map((item) => asText(item))
    : asText(skills).split(/[,\n]/);

  return items
    .map((item) => item.trim())
    .filter(Boolean)
    .join(SKILL_SEPARATOR);
};

// Text for a docx TextRun. Word refuses to open a file whose document.xml
// holds a C0 control character - and \u000b is what Word itself inserts for
// Shift+Enter, so it arrives with every paste from Word; PDF extraction adds
// \u0000 and \f. docx 7 escapes & < > but writes those through as-is, so one
// such character in any field made the whole .docx unreadable while the PDF
// (react-pdf tolerates them) still worked. Editor and AI HTML also landed in
// Word as literal "<strong>" and "&amp;amp;", because docx escapes the
// entities a second time. Real tags are dropped (a bare "<5 years" is not a
// tag and stays), entities are decoded, and undefined/null become "".
const HTML_TAG = /<\/?[a-z][a-z0-9]*\b[^>]*>/gi;
const NAMED_ENTITIES = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
  ndash: "–",
  mdash: "—",
  bull: "•",
  hellip: "…",
  rsquo: "’",
  lsquo: "‘",
  rdquo: "”",
  ldquo: "“",
};

const decodeEntities = (text) =>
  text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, body) => {
    if (body[0] === "#") {
      const code =
        body[1] === "x" || body[1] === "X"
          ? parseInt(body.slice(2), 16)
          : parseInt(body.slice(1), 10);
      return Number.isFinite(code) && code > 0 && code <= 0x10ffff
        ? String.fromCodePoint(code)
        : "";
    }
    const named = NAMED_ENTITIES[body.toLowerCase()];
    return named === undefined ? match : named;
  });

export const docxText = (value) =>
  decodeEntities(
    asText(value)
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(HTML_TAG, "")
  )
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F￾￿]/g, "")
    // A lone surrogate is just as fatal to the XML parser.
    .replace(/[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(^|[^\uD800-\uDBFF])[\uDC00-\uDFFF]/g, "$1");
